import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import {
    Box, Typography, List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
    Avatar, Divider, Paper, CircularProgress, TextField, IconButton,
    Chip, Badge, InputAdornment, Alert, Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import {
    importPublicKey,
    createAndPackageUserKeys,
    decryptPrivateKey,
    encryptMessageForRecipients,
    decryptMessage
} from '../../utils/cryptoUtil';
import ChatMessageContent from '../../components/chat/ChatMessageContent';

// ── Types ────────────────────────────────────────────────────────────────────

interface Contact {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    publicKey?: string;
    preferences?: { photoUrl?: string };
}

interface EncryptedMessage {
    _id: string;
    conversationId: string;
    sender: { _id: string; firstName: string; lastName: string; preferences?: { photoUrl?: string } };
    encryptedContent: string;
    accessKeys: { userId: string; encryptedDEK: string }[];
    readBy: string[];
    createdAt: string;
}

interface DecryptedMessage {
    _id: string;
    sender: EncryptedMessage['sender'];
    content: string;
    createdAt: string;
    isOwn: boolean;
    decryptionFailed?: boolean;
    isPartiallyEncrypted?: boolean;
}

// ── Crypto Helpers ───────────────────────────────────────────────────────────

// Standard crypto helpers are now imported from cryptoUtil.ts

// ── Component ─────────────────────────────────────────────────────────────────

const ChatPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const [trainers, setTrainers] = useState<Contact[]>([]);
    const [managers, setManagers] = useState<Contact[]>([]);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
    const [encryptedMessages, setEncryptedMessages] = useState<EncryptedMessage[]>([]);
    const [decryptedMessages, setDecryptedMessages] = useState<DecryptedMessage[]>([]);
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [decrypting, setDecrypting] = useState(false);
    const [e2eeReady, setE2eeReady] = useState(false);
    const [needsKeySetup, setNeedsKeySetup] = useState(false);
    const [keySetupPassword, setKeySetupPassword] = useState('');
    const [setupLoading, setSetupLoading] = useState(false);
    const [masterPublicKey, setMasterPublicKey] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    // Check if E2EE is ready (private key in memory)
    useEffect(() => {
        if (auth?.privateKey) {
            setE2eeReady(true);
        } else if (auth?.user && !auth.user.encryptedPrivateKey) {
            setNeedsKeySetup(true);
        } else if (auth?.user && auth.user.encryptedPrivateKey && !auth.privateKey) {
            // Has keys stored but not decrypted yet (e.g., after page refresh)
            setNeedsKeySetup(true);
        }
    }, [auth?.privateKey, auth?.user]);

    // Fetch contacts
    useEffect(() => {
        const fetchContacts = async () => {
            try {
                const res = await api.get('/api/chat/contacts');
                setTrainers(res.data.trainers || []);
                setManagers(res.data.managers || []);
            } catch (err) {
                console.error('Error fetching contacts', err);
            } finally {
                setLoading(false);
            }
        };
        if (auth?.user) fetchContacts();
    }, [auth?.user]);

    // Fetch unread counts per contact
    const fetchUnreadByContact = useCallback(async () => {
        try {
            const res = await api.get('/api/chat/unread-by-contact');
            setUnreadCounts(res.data);
        } catch (err) {
            console.error('Error fetching unread counts', err);
        }
    }, []);

    useEffect(() => {
        if (auth?.user) fetchUnreadByContact();
    }, [auth?.user, fetchUnreadByContact]);

    // Mark messages as read when contact is selected
    useEffect(() => {
        if (selectedContact) {
            api.patch(`/api/chat/read-all/${selectedContact._id}`)
                .then(() => {
                    setUnreadCounts(prev => {
                        const newCounts = { ...prev };
                        delete newCounts[selectedContact._id];
                        return newCounts;
                    });
                })
                .catch(console.error);
        }
    }, [selectedContact]);

    // Fetch MasterKey for audit
    useEffect(() => {
        if (auth?.user?.organizationId) {
            const orgId = typeof auth.user.organizationId === 'object'
                ? (auth.user.organizationId as any)?._id
                : auth.user.organizationId;
            api.get(`/api/organizations/public/master-key/${orgId}`)
                .then(res => {
                    if (res.data.isMasterKeySet) setMasterPublicKey(res.data.masterPublicKey);
                })
                .catch(console.error);
        }
    }, [auth?.user?.organizationId]);

    // Fetch messages when contact changes
    useEffect(() => {
        if (!selectedContact) {
            setEncryptedMessages([]);
            return;
        }
        const fetchMessages = async () => {
            setDecrypting(true);
            setEncryptedMessages([]); // Clear immediately
            try {
                const res = await api.get(`/api/chat/messages/${selectedContact._id}`);
                setEncryptedMessages(res.data);
            } catch (err) {
                console.error('Error fetching messages', err);
            } finally {
                setDecrypting(false);
            }
        };
        fetchMessages();
    }, [selectedContact]);

    // Decrypt messages whenever the encrypted list or e2ee key changes
    useEffect(() => {
        if (!auth?.privateKey || encryptedMessages.length === 0) {
            setDecryptedMessages([]); // Always clear if no key or no messages
            return;
        }

        const decryptAll = async () => {
            setDecrypting(true);
            const result: DecryptedMessage[] = [];
            for (const msg of encryptedMessages) {
                const isOwn = msg.sender._id === auth?.user?._id;
                try {
                    const myAccessKey = msg.accessKeys.find(k => k.userId === auth?.user?._id);
                    if (!myAccessKey) {
                        result.push({
                            _id: msg._id,
                            sender: msg.sender,
                            content: '[Message non accessible — clé manquante]',
                            createdAt: msg.createdAt,
                            isOwn,
                            decryptionFailed: true
                        });
                        continue;
                    }
                    
                    const content = await decryptMessage(msg.encryptedContent, myAccessKey.encryptedDEK, auth.privateKey!);
                    
                    // Check for partial encryption (missing auditor/master key)
                    let isPartiallyEncrypted = false;
                    if (masterPublicKey) { // Only check if master key is set
                        const orgId = typeof auth?.user?.organizationId === 'object' 
                            ? (auth?.user?.organizationId as any)?._id 
                            : auth?.user?.organizationId;
                        if (orgId && !msg.accessKeys.some(k => k.userId === orgId)) {
                            isPartiallyEncrypted = true;
                        }
                    }

                    result.push({
                        _id: msg._id,
                        sender: msg.sender,
                        content,
                        createdAt: msg.createdAt,
                        isOwn,
                        isPartiallyEncrypted
                    });
                } catch (e) {
                    console.error('Decryption error:', e);
                    result.push({
                        _id: msg._id,
                        sender: msg.sender,
                        content: '', // Content is useless if decryption failed
                        createdAt: msg.createdAt,
                        isOwn,
                        decryptionFailed: true
                    });
                }
            }
            setDecryptedMessages(result);
            setDecrypting(false);
        };

        decryptAll();
    }, [encryptedMessages, auth?.privateKey, auth?.user]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [decryptedMessages]);

    const handleResend = (content: string) => {
        if (!content) {
            showSnackbar("Contenu du message perdu, impossible de renvoyer.", "error");
            return;
        }
        setMessageInput(content);
        const inputElement = document.getElementById('chat-input');
        if (inputElement) inputElement.focus();
        showSnackbar("Message copié dans le champ de saisie pour renvoi.", "info");
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim() || !selectedContact || !auth?.privateKey || !auth?.user) return;
        setSending(true);

        try {
            // Get recipient's public key
            const recipientKeyRes = await api.get(`/api/chat/public-key/${selectedContact._id}`);
            if (!recipientKeyRes.data.publicKey) {
                showSnackbar("Ce contact n'a pas encore de clé E2EE — il doit se connecter d'abord.", 'error');
                return;
            }

            const recipients = [
                { userId: auth.user._id, publicKey: auth.user.publicKey! },
                { userId: selectedContact._id, publicKey: recipientKeyRes.data.publicKey }
            ];

            if (masterPublicKey) {
                recipients.push({ userId: auth.user.organizationId as any, publicKey: masterPublicKey });
            }

            const { encryptedContent, accessKeys } = await encryptMessageForRecipients(messageInput.trim(), recipients);

            const res = await api.post('/api/chat/messages', {
                recipientId: selectedContact._id,
                encryptedContent,
                accessKeys
            });

            // Optimistically add the decrypted version
            setDecryptedMessages(prev => [...prev, {
                _id: res.data._id,
                sender: { _id: auth.user!._id, firstName: auth.user!.firstName, lastName: auth.user!.lastName },
                content: messageInput.trim(),
                createdAt: new Date().toISOString(),
                isOwn: true
            }]);
            setEncryptedMessages(prev => [...prev, res.data]);
            setMessageInput('');
        } catch (err: any) {
            console.error('Send error', err);
            showSnackbar(err?.response?.data?.message || 'Erreur lors de l\'envoi', 'error');
        } finally {
            setSending(false);
        }
    };

    const handleSetupKeys = async () => {
        if (!keySetupPassword || !auth?.user) return;
        setSetupLoading(true);

        try {
            if (auth.user.encryptedPrivateKey) {
                // Unlock existing keys
                const key = await decryptPrivateKey(auth.user.encryptedPrivateKey, keySetupPassword);
                auth.setPrivateKey(key);
                setNeedsKeySetup(false);
                setE2eeReady(true);
                showSnackbar('Clé E2EE déverrouillée avec succès ✅', 'success');
            } else {
                // Generate new keys
                const keys = await createAndPackageUserKeys(keySetupPassword);
                await api.put('/api/chat/register-keys', keys);
                // Re-fetch user to get the new keys
                const updatedUser = { ...auth.user, publicKey: keys.publicKey, encryptedPrivateKey: keys.encryptedPrivateKey };
                auth.updateUser(updatedUser as any);
                // Unlock
                const key = await decryptPrivateKey(keys.encryptedPrivateKey, keySetupPassword);
                auth.setPrivateKey(key);
                setNeedsKeySetup(false);
                setE2eeReady(true);
                showSnackbar('Clé E2EE générée et activée ✅', 'success');
            }
        } catch (err) {
            console.error('Key setup error', err);
            showSnackbar('Mot de passe incorrect ou erreur de clé', 'error');
        } finally {
            setSetupLoading(false);
            setKeySetupPassword('');
        }
    };

    const formatTime = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (d.toDateString() === yesterday.toDateString()) return 'Hier';
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    };

    const groupedMessages = decryptedMessages.reduce<Record<string, DecryptedMessage[]>>((acc, msg) => {
        const key = formatDate(msg.createdAt);
        if (!acc[key]) acc[key] = [];
        acc[key].push(msg);
        return acc;
    }, {});

    // ── Key Setup Panel ─────────────────────────────────────────────────────────
    if (needsKeySetup && !e2eeReady) {
        return (
            <Box sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 4, maxWidth: 480, width: '100%', textAlign: 'center',
                        border: '1px solid', borderColor: 'divider', borderRadius: 3
                    }}
                >
                    <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <VpnKeyIcon sx={{ fontSize: 36, color: 'white' }} />
                    </Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {auth?.user?.encryptedPrivateKey ? 'Déverrouiller vos messages sécurisés' : 'Activer le chat E2EE'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {auth?.user?.encryptedPrivateKey
                            ? 'Entrez votre mot de passe pour déchiffrer votre clé privée et accéder à vos messages.'
                            : 'Vous êtes sur le point de générer une paire de clés RSA chiffrée. Votre clé privée ne quittera jamais votre navigateur.'}
                    </Typography>

                    <Alert severity="info" sx={{ mb: 3, textAlign: 'left', fontSize: '0.8rem' }}>
                        <strong>Chiffrement de bout en bout (E2EE)</strong> — Aucun message n'est lisible par le serveur.
                    </Alert>

                    <TextField
                        type="password"
                        fullWidth
                        label="Votre mot de passe de connexion"
                        value={keySetupPassword}
                        onChange={e => setKeySetupPassword(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetupKeys(); }}
                        sx={{ mb: 2 }}
                        disabled={setupLoading}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, cursor: 'pointer', py: 1.5, px: 3, borderRadius: 2, background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', fontWeight: 600, fontSize: '1rem', opacity: setupLoading ? 0.7 : 1 }}
                        onClick={setupLoading ? undefined : handleSetupKeys}
                    >
                        {setupLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <LockOpenIcon />}
                        {auth?.user?.encryptedPrivateKey ? 'Déverrouiller' : 'Générer mes clés E2EE'}
                    </Box>
                </Paper>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        );
    }

    // ── Main Chat View ──────────────────────────────────────────────────────────
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2 }, gap: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" fontWeight={700}>Mes Messages</Typography>
                <Chip
                    icon={e2eeReady ? <LockIcon sx={{ fontSize: '14px !important' }} /> : <LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                    label={e2eeReady ? 'E2EE Actif' : 'Non sécurisé'}
                    size="small"
                    color={e2eeReady ? 'success' : 'warning'}
                    sx={{ fontSize: '0.7rem' }}
                />
            </Box>

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1.5, pt: 1.5 }}>
                {/* Contacts Sidebar */}
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: 240, md: 300 }, flexShrink: 0,
                        border: '1px solid', borderColor: 'divider', borderRadius: 2,
                        overflow: 'auto', display: 'flex', flexDirection: 'column'
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <>
                            {/* Trainers */}
                            <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(90deg, #667eea18, transparent)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SchoolIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                        Formateurs
                                    </Typography>
                                </Box>
                            </Box>
                            <List dense disablePadding>
                                {trainers.length === 0 ? (
                                    <ListItem sx={{ py: 1 }}>
                                        <ListItemText secondary="Aucun formateur attribué" />
                                    </ListItem>
                                ) : trainers.map(t => (
                                    <ListItemButton
                                        key={t._id}
                                        selected={selectedContact?._id === t._id}
                                        onClick={() => setSelectedContact(t)}
                                        sx={{ py: 1.5 }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                            <Avatar
                                                src={t.preferences?.photoUrl}
                                                sx={{ width: 32, height: 32, fontSize: '0.8rem', background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
                                            >
                                                {t.firstName[0]}{t.lastName[0]}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={`${t.firstName} ${t.lastName}`}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                                        />
                                        {unreadCounts[t._id] > 0 && (
                                            <Badge badgeContent={unreadCounts[t._id]} color="error" sx={{ mr: 1 }} />
                                        )}
                                        {!t.publicKey && <LockOpenIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
                                    </ListItemButton>
                                ))}
                            </List>
                            <Divider />

                            {/* Managers */}
                            <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(90deg, #764ba218, transparent)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                                    <Typography variant="caption" fontWeight={700} color="secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                        Référents Péd.
                                    </Typography>
                                </Box>
                            </Box>
                            <List dense disablePadding>
                                {managers.length === 0 ? (
                                    <ListItem sx={{ py: 1 }}>
                                        <ListItemText secondary="Aucun référent attribué" />
                                    </ListItem>
                                ) : managers.map(m => (
                                    <ListItemButton
                                        key={m._id}
                                        selected={selectedContact?._id === m._id}
                                        onClick={() => setSelectedContact(m)}
                                        sx={{ py: 1.5 }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                            <Avatar
                                                src={m.preferences?.photoUrl}
                                                sx={{ width: 32, height: 32, fontSize: '0.8rem', background: 'linear-gradient(135deg, #764ba2, #b06ab3)' }}
                                            >
                                                {m.firstName[0]}{m.lastName[0]}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={`${m.firstName} ${m.lastName}`}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                                        />
                                        {unreadCounts[m._id] > 0 && (
                                            <Badge badgeContent={unreadCounts[m._id]} color="error" sx={{ mr: 1 }} />
                                        )}
                                        {!m.publicKey && <LockOpenIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
                                    </ListItemButton>
                                ))}
                            </List>
                        </>
                    )}
                </Paper>

                {/* Chat Area */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden'
                    }}
                >
                    {!selectedContact ? (
                        /* Empty State */
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, opacity: 0.5 }}>
                            <LockIcon sx={{ fontSize: 64, mb: 2, color: 'primary.main' }} />
                            <Typography variant="h6" fontWeight={600} align="center">Sélectionnez un contact</Typography>
                            <Typography variant="body2" color="text.secondary" align="center">
                                Vos messages sont chiffrés de bout en bout.<br />Seuls vous et votre interlocuteur pouvez les lire.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Chat Header */}
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, background: 'linear-gradient(90deg, #667eea08, #764ba208)' }}>
                                <Avatar
                                    src={selectedContact.preferences?.photoUrl}
                                    sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
                                >
                                    {selectedContact.firstName[0]}{selectedContact.lastName[0]}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {selectedContact.firstName} {selectedContact.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{selectedContact.email}</Typography>
                                </Box>
                                {selectedContact.publicKey
                                    ? <Chip icon={<LockIcon sx={{ fontSize: '12px !important' }} />} label="E2EE" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                    : <Chip icon={<LockOpenIcon sx={{ fontSize: '12px !important' }} />} label="Sans clé" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                }
                            </Box>

                            {/* Messages */}
                            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {decrypting ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : !e2eeReady ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                                        <Alert severity="warning">Déverrouillez vos clés E2EE pour voir les messages.</Alert>
                                    </Box>
                                ) : decryptedMessages.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                                        <Typography variant="body2" color="text.secondary">Aucun message — démarrez la conversation !</Typography>
                                    </Box>
                                ) : (
                                    Object.entries(groupedMessages).map(([dateLabel, msgs]) => (
                                        <Box key={dateLabel}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                                                <Divider sx={{ flex: 1 }} />
                                                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', px: 1 }}>{dateLabel}</Typography>
                                                <Divider sx={{ flex: 1 }} />
                                            </Box>
                                            {msgs.map(msg => (
                                                <Box
                                                    key={msg._id}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: msg.isOwn ? 'flex-end' : 'flex-start',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {!msg.isOwn && (
                                                        <Avatar sx={{ width: 28, height: 28, mr: 1, alignSelf: 'flex-end', mb: 0.5, fontSize: '0.7rem', background: 'linear-gradient(135deg, #667eea, #764ba2)', flexShrink: 0 }}>
                                                            {msg.sender.firstName[0]}{msg.sender.lastName[0]}
                                                        </Avatar>
                                                    )}
                                                    <Box sx={{ maxWidth: '70%' }}>
                                                        <Box
                                                            sx={{
                                                                px: 1.5, py: 1,
                                                                borderRadius: msg.isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                                background: msg.isOwn
                                                                    ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                                                    : 'background.paper',
                                                                border: msg.isOwn ? 'none' : '1px solid',
                                                                borderColor: 'divider',
                                                                color: msg.isOwn ? 'white' : 'text.primary',
                                                                boxShadow: msg.isOwn ? '0 2px 8px rgba(102,126,234,0.3)' : 'none'
                                                            }}
                                                        >
                                                            <ChatMessageContent 
                                                                content={msg.content} 
                                                                isOwn={msg.isOwn} 
                                                                decryptionFailed={msg.decryptionFailed}
                                                                isPartiallyEncrypted={msg.isPartiallyEncrypted}
                                                                onResend={() => handleResend(msg.content)}
                                                            />
                                                        </Box>
                                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.3, px: 0.5, textAlign: msg.isOwn ? 'right' : 'left' }}>
                                                            {formatTime(msg.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    ))
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input */}
                            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                {!e2eeReady ? (
                                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                        Vous devez déverrouiller vos clés E2EE pour envoyer des messages.
                                    </Alert>
                                ) : !selectedContact.publicKey ? (
                                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                        Ce contact n'a pas encore activé son chat E2EE — les messages ne peuvent pas encore être chiffrés.
                                    </Alert>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            maxRows={4}
                                            size="small"
                                            placeholder="Votre message chiffré E2EE..."
                                            value={messageInput}
                                            onChange={e => setMessageInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            disabled={sending}
                                            sx={{
                                                '& .MuiOutlinedInput-root': { borderRadius: 3 },
                                                '& textarea': { fontSize: '0.9rem' }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <IconButton
                                            onClick={handleSendMessage}
                                            disabled={!messageInput.trim() || sending}
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                                color: 'white',
                                                width: 40, height: 40,
                                                '&:hover': { background: 'linear-gradient(135deg, #5a6fd8, #6a3d99)' },
                                                '&.Mui-disabled': { background: 'grey.200', color: 'grey.400' }
                                            }}
                                        >
                                            {sending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ChatPage;
